import React, { useEffect, useState } from 'react';
import {
    CargoCol,
    CargoInputWrapper,
    CargoFieldGroup,
    CargoTankFieldsRow,
    CargoTankWrapper,
    OnBoardWrapper,
    CommentWrapper,
    CargoInputField,
} from './CargoTempLogReporting.style';
import { TankDetails, inputTypes } from '../CargoTemperatureLogUtils';
import { CargoTemperatureLogEntry } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureLog';
import OdfjellInputComponent, { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { getHoursMinutesFromDate, isDateSameOrAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import CategoryElement from '../cargo-category/CategoryElement';
import CargoLogInput from './CargoLogInput';
import { CargoTemperatureConfig } from 'src/types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { CargoTempCategory } from 'src/types/ViewModel/CargoTempLog/CargoInTankItem';
import { isInhibitorQuestionnaireCompleted } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { StatusIcon } from 'src/components/ui/status-icon/NoonReportStatusIcon.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppStore } from 'src/context/AppContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';

type CargoLogRowProps = {
    tankDetails: TankDetails;
    index: number;
    activeLogSchedules: string[];
    isCommentsVisible: boolean;
    isReadOnly: boolean;
    oxygenUnitType: string;
    editComment: boolean;
    editCommentCargoLog: CargoTemperatureLogEntry;
    commentInput: string;
    onCargoLogValueChange: (changeObject: InputChangeData) => void;
    onCargoLogIsOnBoardChange: (event: React.ChangeEvent<HTMLInputElement>, cargoLog: CargoTemperatureLogEntry) => void;
    onCargoLogCommentChange: (comment: string, cargoLog: CargoTemperatureLogEntry) => void;
    openComment: (cargoLog: CargoTemperatureLogEntry) => void;
    cargoTanks: TankDetails[];
    getNumberOfInputFields: (cargoNumber: string) => number;
    config: CargoTemperatureConfig;
    date: Date;
};

const CargoLogRow: React.FC<CargoLogRowProps> = ({
    tankDetails,
    index,
    activeLogSchedules,
    isCommentsVisible,
    isReadOnly,
    oxygenUnitType,
    editComment,
    editCommentCargoLog,
    commentInput,
    onCargoLogValueChange,
    onCargoLogIsOnBoardChange,
    onCargoLogCommentChange,
    openComment,
    cargoTanks,
    getNumberOfInputFields,
    config,
    date,
}) => {
    const { appFeatureSettings } = useAppStore();
    const [inhibitorQuestionnaireEnabled, setInhibitorQuestionnaireEnabled] = useState<boolean>(false);

    useEffect(() => {
        if (appFeatureSettings) {
            const cargoTempLogQuestionnaire = appFeatureSettings.find((x) => x.appFeatureId === AppFeatureEnum.CargoTempLogQuestionnaire);
            if (cargoTempLogQuestionnaire && isDateSameOrAfter(date, cargoTempLogQuestionnaire.fromDate)) {
                setInhibitorQuestionnaireEnabled(true);
            } else {
                setInhibitorQuestionnaireEnabled(false);
            }
        }
    }, [appFeatureSettings]);

    const renderCargoLogGroups = () => {
        return activeLogSchedules.map((logTime, k) => {
            const cargoLogIndex = tankDetails.cargoLogs.findIndex(
                (x) => x?.[0]?.loggedTimeLocalTime && getHoursMinutesFromDate(x[0].loggedTimeLocalTime) === logTime
            );
            const cargoLog = cargoLogIndex !== -1 ? tankDetails.cargoLogs[cargoLogIndex] : null;

            if (!cargoLog) {
                return (
                    <CargoInputWrapper key={`tankcargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}`}>
                        <CargoFieldGroup
                            showComments={isCommentsVisible}
                            key={`tankcargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}`}
                        />
                    </CargoInputWrapper>
                );
            }

            return (
                <CargoInputWrapper key={`tankcargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}`}>
                    <CargoFieldGroup showComments={isCommentsVisible}>
                        <OnBoardWrapper>
                            <OdfjellInputComponent
                                key={`tankcargoLog_isOnBoard_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}`}
                                type={'checkbox'}
                                name={'isOnBoard'}
                                value={cargoLog[0].isOnBoard}
                                onChange={(changeObject) => onCargoLogIsOnBoardChange(changeObject.event, cargoLog[0])}
                                isReadOnly={isReadOnly}
                                dataObject={cargoLog[0]}
                                disableUnit
                            />
                        </OnBoardWrapper>
                        {cargoLog[0].isOnBoard
                            ? inputTypes.map((_, l) => {
                                  const fieldId = l + 1;
                                  const cargoLogItem = cargoLog.find((x) => x.cargoTemperatureFieldId === fieldId);
                                  const className = fieldId === 2 ? 'unit-col' : '';
                                  return (
                                      <CargoLogInput
                                          key={`cargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}_${l}`}
                                          cargoLog={cargoLogItem}
                                          onChange={onCargoLogValueChange}
                                          isReadOnly={isReadOnly}
                                          fieldId={fieldId}
                                          openComment={openComment}
                                          isCommentsVisible={isCommentsVisible}
                                          oxygenUnitType={oxygenUnitType}
                                          tabIndex={(k + 1) * 4000 + (fieldId - 1) * 800 + index}
                                          className={className}
                                      />
                                  );
                              })
                            : cargoLog.map((_, l) => (
                                  <CargoInputField key={`cargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${l}`} />
                              ))}
                    </CargoFieldGroup>
                    {cargoLog[0].isOnBoard &&
                        isCommentsVisible &&
                        editComment &&
                        editCommentCargoLog?.tankId === cargoLog[0].tankId &&
                        getHoursMinutesFromDate(editCommentCargoLog.loggedTimeLocalTime) === logTime && (
                            <CommentWrapper key={`tankcargoLog_${tankDetails.cargoNumber}_${tankDetails.tank.tankName}_${k}_comment`}>
                                <OdfjellInputComponent
                                    type={'textarea'}
                                    value={commentInput}
                                    isReadOnly={isReadOnly}
                                    dataObject={commentInput}
                                    onChange={(changeObject) => onCargoLogCommentChange(changeObject.event.target.value, editCommentCargoLog)}
                                    disableUnit
                                    validationInTooltip
                                />
                            </CommentWrapper>
                        )}
                </CargoInputWrapper>
            );
        });
    };

    return (
        <div key={`cargo_tank${tankDetails.cargoNumber}_${index}`}>
            <CargoTankWrapper key={`cargo_tank_wrapper${tankDetails.tank}_${index}`}>
                <CargoCol className="cargo-nr">{tankDetails.cargoNumber}</CargoCol>
                <CargoCol className="trade-name">{tankDetails.tradeName}</CargoCol>
                <CargoCol className="tank-name">{tankDetails.tank.tankName}</CargoCol>
                <CargoCol className="category">
                    <CategoryElement cargoTanks={cargoTanks} cargoNumber={tankDetails.cargoNumber} />
                    {inhibitorQuestionnaireEnabled &&
                        cargoTanks.find((c) => c.cargoNumber === tankDetails.cargoNumber)?.category === CargoTempCategory.Inhibited &&
                        !isInhibitorQuestionnaireCompleted(config) && (
                            <StatusIcon className="status-icon status-missing">
                                <FontAwesomeIcon icon="exclamation-circle" title="Missing Inhibitor configuration" />
                            </StatusIcon>
                        )}
                </CargoCol>
                <CargoCol className="unit">{oxygenUnitType}</CargoCol>
                <CargoCol className="fields">
                    <CargoTankFieldsRow numberOfFields={getNumberOfInputFields(tankDetails.cargoNumber)}>{renderCargoLogGroups()}</CargoTankFieldsRow>
                </CargoCol>
            </CargoTankWrapper>
        </div>
    );
};

export default CargoLogRow;
